import React, { useEffect, useState } from 'react';
import { Card, Typography, Tag, Button, message, Modal } from 'antd';
import { InfoCircleOutlined, ArrowRightOutlined, DownloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTender } from '../../contexts/TenderContext';
import { useDevMode } from '@/contexts/DevModeContext';
import { useScrollToTop } from '../../hooks/useScrollToTop';
import MainLayout from '../layout/MainLayout';
import NumberInput from '../common/NumberInput';
import { ExcelImportModal } from '../common/ExcelImportExport';
import { COLORS } from '../../utils/constants';
import { parseSpoilReconciliationFromExcel } from '../../utils/spoilReconciliationExcelUtils';
import {
  buildSpoilReconciliationSummary,
  emptySpoilUserInputs,
  type SpoilUserInputs,
} from '../../utils/spoilReconciliationSummary';

const { Title, Text } = Typography;

const emptyUserInputs = emptySpoilUserInputs;

type UserFields = SpoilUserInputs;

const thStyle: React.CSSProperties = {
  backgroundColor: COLORS.tableHeaderBg,
  fontWeight: 600,
  color: '#000',
  padding: '12px 16px',
  textAlign: 'center',
  fontSize: '13px',
  borderRight: '1px solid #e5e7eb',
  borderBottom: '1px solid #e5e7eb',
};

const tdLabelStyle: React.CSSProperties = {
  padding: '8px 16px',
  borderRight: '1px solid #e5e7eb',
  borderBottom: '1px solid #e5e7eb',
  fontSize: '14px',
};

const tdInputStyle: React.CSSProperties = {
  padding: '4px 8px',
  borderRight: '1px solid #e5e7eb',
  borderBottom: '1px solid #e5e7eb',
};

const tdCalcStyle: React.CSSProperties = {
  padding: '8px 16px',
  textAlign: 'right',
  borderRight: '1px solid #e5e7eb',
  borderBottom: '1px solid #e5e7eb',
  fontSize: '14px',
  backgroundColor: '#f5f5f5',
  color: '#595959',
};

const totalRowStyle: React.CSSProperties = {
  backgroundColor: '#FFF7E6',
  fontWeight: 600,
};

const sectionHeadingStyle: React.CSSProperties = {
  fontWeight: 600,
  fontSize: '14px',
  color: '#374151',
  marginTop: '20px',
  marginBottom: '8px',
};

interface InfoModalContent {
  title: string;
}

const renderBalanceBadge = (balance: number) => {
  if (balance > 0) return <Tag color="green" style={{ fontWeight: 600, fontSize: '12px' }}>Surplus</Tag>;
  if (balance < 0) return <Tag color="red" style={{ fontWeight: 600, fontSize: '12px' }}>Deficit</Tag>;
  return null;
};

const SpoilReconciliation: React.FC = () => {
  useScrollToTop({ behavior: 'smooth', delay: 100 });
  const { state, updateField, saveTender } = useTender();
  const navigate = useNavigate();
  const showDevMode = useDevMode();

  const [inputs, setInputs] = useState<UserFields>(() => ({
    ...emptyUserInputs(),
    ...(state.spoilReconciliation?.config || {}),
  }));
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [activeInfo, setActiveInfo] = useState<InfoModalContent | null>(null);

  // Defined inside the component so it can open the shared activeInfo modal
  // via closure — clicking shows a Modal with the title text instead of a
  // hover tooltip.
  const InfoIcon: React.FC<InfoModalContent> = ({ title }) =>
    showDevMode ? (
      <InfoCircleOutlined
        style={{ color: '#1890ff', marginLeft: '6px', fontSize: '13px', cursor: 'pointer' }}
        onClick={() => setActiveInfo({ title })}
      />
    ) : null;

  useEffect(() => {
    if (state.spoilReconciliation?.config) {
      setInputs((prev) => ({ ...emptyUserInputs(), ...state.spoilReconciliation?.config, ...prev }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = (field: keyof UserFields, value: number) => {
    const next = { ...inputs, [field]: value };
    setInputs(next);
    updateField('spoilReconciliation', { ...state.spoilReconciliation, config: { ...(state.spoilReconciliation?.config || {}), ...next } });
  };

  // Imports the manual-input Ponds/POS/Attenuation Tanks/Culverts/Mains
  // Services quantities from the 'Spoil Reconcilliation' sheet's E/F
  // columns. Rows that are computed from Roads/Foul/Storm state (Road /
  // Footpaths, Road, Paths, Foul Sewers, Storm Sewers) have no mapping in
  // parseSpoilReconciliationFromExcel, so a matching sheet row is skipped
  // rather than overwriting the calculated value.
  const handleImport = async (file: File) => {
    setImportLoading(true);
    try {
      const imported = await parseSpoilReconciliationFromExcel(file);
      if (Object.keys(imported).length === 0) {
        alert('No matching rows found in "Spoil Reconcilliation" sheet. Check that the sheet exists and the Topsoil/Subsoil section headers are present.');
        return;
      }
      const next = { ...inputs, ...imported } as UserFields;
      setInputs(next);
      updateField('spoilReconciliation', { ...state.spoilReconciliation, config: { ...(state.spoilReconciliation?.config || {}), ...next } });
      setImportModalVisible(false);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error('Spoil Reconciliation import failed:', err);
      alert(`Import failed: ${msg}`);
    } finally {
      setImportLoading(false);
    }
  };

  // Every calculated figure on this page (Roads topsoil/cut, Ponds & Swales,
  // Foul/Storm surplus) plus the totals — see `spoilReconciliationSummary.ts`.
  // Shared with Review & Calculate's Run Matrix "Spoil" section so the two can
  // never drift.
  const {
    roadTopsoilStrip, roadTopsoilReuse,
    topsoilPondsStrip, topsoilPondsReuse,
    subsoilRoadCut, subsoilPathsCut, subsoilPathsFill,
    subsoilFoulCut, subsoilStormCut,
    subsoilPondsCut, subsoilPondsFill,
    totalTopsoilStrip, totalTopsoilReuse, topsoilSurplusDeficit,
    totalSubsoilCut, totalSubsoilFill, subsoilSurplusDeficit,
  } = buildSpoilReconciliationSummary({
    roads: state.roads,
    foul: {
      pipeRuns: state.foulSewers?.pipeRuns.table || [],
      manholes: state.foulSewers?.manholes.table || [],
      laterals: state.foulSewers?.lateralConnections.table || [],
    },
    storm: {
      pipeRuns: state.stormSewers?.pipeRuns.table || [],
      manholes: state.stormSewers?.manholes.table || [],
      laterals: state.stormSewers?.lateralConnections.table || [],
      gullies: state.stormSewers?.gullyConnections.table || [],
    },
    pondsSwalesTable: state.pondsSwales?.table,
    inputs,
  }).derived;

  const handleSaveAndContinue = async () => {
    try {
      message.loading({ content: 'Saving tender...', key: 'spoilSave', duration: 0 });
      updateField('spoilReconciliation', { ...state.spoilReconciliation, config: { ...(state.spoilReconciliation?.config || {}), ...inputs } });
      updateField('status', 'In-progress');
      updateField('boqStatus', 'In-progress');
      updateField('lastModified', new Date());
      await saveTender();
      message.success({ content: 'Saved successfully', key: 'spoilSave', duration: 2 });
      setTimeout(() => navigate('/roads-sewers/compound'), 300);
    } catch {
      message.error({ content: 'Save failed', key: 'spoilSave', duration: 3 });
    }
  };

  const handleSaveAndExit = async () => {
    try {
      message.loading({ content: 'Saving tender...', key: 'spoilSave', duration: 0 });
      updateField('spoilReconciliation', { ...state.spoilReconciliation, config: { ...(state.spoilReconciliation?.config || {}), ...inputs } });
      updateField('status', 'In-progress');
      updateField('boqStatus', 'In-progress');
      updateField('lastModified', new Date());
      await saveTender();
      message.success({ content: 'Saved successfully', key: 'spoilSave', duration: 2 });
      setTimeout(() => navigate('/'), 300);
    } catch {
      message.error({ content: 'Save failed', key: 'spoilSave', duration: 3 });
    }
  };

  const calcCell = (value: number) => (
    <td style={tdCalcStyle}>{value.toFixed(3)}</td>
  );

  const inputCell = (field: keyof UserFields) => (
    <td style={tdInputStyle}>
      <NumberInput
        value={inputs[field]}
        onChange={(v) => update(field, typeof v === 'number' ? v : parseFloat(v as string) || 0)}
        decimalPlaces={3}
        placeholder="0.000"
      />
    </td>
  );

  return (
    <MainLayout canProceed={true} showFooter={false}>
      <Card
        className="spoil-card shadow-sm"
        bodyStyle={{ padding: 0 }}
        style={{ borderRadius: '8px', overflow: 'visible' }}
      >
        {/* Sticky header */}
        <div className="spoil-sticky-header" style={{ position: 'sticky', top: '144px', backgroundColor: '#fff', zIndex: 98, borderRadius: '8px 8px 0 0', isolation: 'isolate' }}>
        <div className="px-6 pt-6 pb-4" style={{ borderBottom: '1px solid #e5e7eb' }}>
          <div className="flex justify-between items-center">
            <div>
              <Title level={3} style={{ fontWeight: 600, fontSize: '20px', color: '#111827', marginBottom: '4px' }}>
                Spoil Reconciliation
              </Title>
              <Text type="secondary" style={{ fontSize: '14px', color: '#6B7280' }}>
                Reconcile topsoil strip &amp; re-use and subsoil cut &amp; fill volumes across all works.
                Shaded rows are auto-calculated from Roads, Foul Sewers, and Storm Sewers data.
              </Text>
            </div>
            {showDevMode && (
              <Button
                type="text"
                icon={<DownloadOutlined />}
                onClick={() => setImportModalVisible(true)}
                style={{ color: '#1890ff', fontWeight: 500 }}
              >
                Import Take-off
              </Button>
            )}
          </div>
        </div>
        </div>{/* end spoil-sticky-header */}

        {/* Content */}
        <div className="px-6 pb-6" style={{ paddingTop: '16px' }}>

          {/* ── Topsoil Table ─────────────────────────────────────── */}
          <div style={sectionHeadingStyle}>Topsoil</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #e5e7eb' }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, textAlign: 'left', width: '40%' }}></th>
                <th style={thStyle}>Strip (m³)</th>
                <th style={thStyle}>Re-use (m³)</th>
              </tr>
            </thead>
            <tbody>
              {/* Road/Footpaths — computed from Roads data */}
              <tr>
                <td style={tdLabelStyle}>
                  Road / Footpaths
                  <InfoIcon title="Strip: SUM of 'Excavate to reduce levels to take up Xmm top soil, deposit onsite' across every road, from Roads → Carriageway Summary. Re-use: Total verge area × the verge topsoil thickness set on Footpaths — Construction Configuration." />
                </td>
                {calcCell(roadTopsoilStrip)}
                {calcCell(roadTopsoilReuse)}
              </tr>
              {/* Ponds — computed from Storm Sewers' Ponds & Swales build-up */}
              <tr>
                <td style={tdLabelStyle}>
                  Ponds
                  <InfoIcon title="Strip: SUM of 'Excavate to reduce levels to take up 279mm top soil, deposit onsite' across every Pond and Swale. Re-use: SUM of 'Spread and level 150mm thick top soil' across every Pond and Swale, × 0.15. Source: Storm Sewers → Ponds & Swales build-up." />
                </td>
                {calcCell(topsoilPondsStrip)}
                {calcCell(topsoilPondsReuse)}
              </tr>
              {/* POS — user input */}
              <tr>
                <td style={tdLabelStyle}>POS</td>
                {inputCell('topsoilPOSStrip')}
                {inputCell('topsoilPOSReuse')}
              </tr>
              {/* Attenuation Tanks — user input */}
              <tr>
                <td style={tdLabelStyle}>Attenuation Tanks</td>
                {inputCell('topsoilAttenuationTanksStrip')}
                {inputCell('topsoilAttenuationTanksReuse')}
              </tr>
              {/* Culverts — user input */}
              <tr>
                <td style={tdLabelStyle}>Culverts</td>
                {inputCell('topsoilCulvertsStrip')}
                {inputCell('topsoilCulvertsReuse')}
              </tr>
            </tbody>
            <tfoot>
              <tr style={totalRowStyle}>
                <td style={{ ...tdLabelStyle, ...totalRowStyle }}>Total</td>
                <td style={{ ...tdCalcStyle, ...totalRowStyle }}>{totalTopsoilStrip.toFixed(3)}</td>
                <td style={{ ...tdCalcStyle, ...totalRowStyle }}>{totalTopsoilReuse.toFixed(3)}</td>
              </tr>
              <tr>
                <td style={{ ...tdLabelStyle, fontWeight: 700, borderBottom: 'none' }}>
                  Surplus / Deficit
                  <InfoIcon title="Strip Total − Re-use Total. Positive = Surplus (disposal needed); Negative = Deficit (import needed)." />
                </td>
                <td colSpan={2} style={{ ...tdCalcStyle, fontWeight: 700, borderBottom: 'none', borderRight: 'none', backgroundColor: '#fff' }}>
                  <span style={{ marginRight: '10px' }}>{topsoilSurplusDeficit.toFixed(3)}</span>
                  {renderBalanceBadge(topsoilSurplusDeficit)}
                </td>
              </tr>
            </tfoot>
          </table>

          {/* ── Subsoil Table ─────────────────────────────────────── */}
          <div style={sectionHeadingStyle}>Subsoil</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #e5e7eb' }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, textAlign: 'left', width: '40%' }}></th>
                <th style={thStyle}>Cut / Arisings (m³)</th>
                <th style={thStyle}>Fill (m³)</th>
              </tr>
            </thead>
            <tbody>
              {/* Road — cut computed from Roads cut-fill sections, fill is user input */}
              <tr>
                <td style={tdLabelStyle}>
                  Road
                  <InfoIcon title="Cut: computed from Roads → Cut & Fill sections (sum of carriageway cut volumes). Fill: user input. Included in Total." />
                </td>
                {calcCell(subsoilRoadCut)}
                {inputCell('subsoilRoadFill')}
              </tr>
              {/* Paths — cut & fill, computed from Roads cut-fill sections */}
              <tr>
                <td style={tdLabelStyle}>
                  Paths
                  <InfoIcon title="Cut: sum of footpaths cut volumes from Roads → Cut & Fill sections. Fill: sum of footpaths fill volumes. Included in Total." />
                </td>
                {calcCell(subsoilPathsCut)}
                {calcCell(subsoilPathsFill)}
              </tr>
              {/* Foul Sewers — cut only, computed from Foul Sewers pipe runs */}
              <tr>
                <td style={tdLabelStyle}>
                  Foul Sewers
                  <InfoIcon title="Grand total Surplus from the Foul Sewers → Foul Summary tab's Surplus table (Runs + Lateral + Manholes, per pipe run). Included in sub-total." />
                </td>
                {calcCell(subsoilFoulCut)}
                <td style={tdCalcStyle}>—</td>
              </tr>
              {/* Storm Sewers — cut only, computed from Storm Sewers pipe runs */}
              <tr>
                <td style={tdLabelStyle}>
                  Storm Sewers
                  <InfoIcon title="Grand total Surplus from the Storm Sewers → Storm Summary tab's Surplus table (Runs + Lateral + Manholes + Gullies, per pipe run). Included in sub-total." />
                </td>
                {calcCell(subsoilStormCut)}
                <td style={tdCalcStyle}>—</td>
              </tr>
              {/* Ponds — computed from Storm Sewers' Ponds & Swales build-up */}
              <tr>
                <td style={tdLabelStyle}>
                  Ponds
                  <InfoIcon title="Cut / Arisings: SUM of 'Excavate to reduce levels' across every Pond and Swale. Fill: SUM of 'Filling with site won subsoil' across every Pond and Swale. Source: Storm Sewers → Ponds & Swales build-up." />
                </td>
                {calcCell(subsoilPondsCut)}
                {calcCell(subsoilPondsFill)}
              </tr>
              {/* POS — user input */}
              <tr>
                <td style={tdLabelStyle}>POS</td>
                {inputCell('subsoilPOSCut')}
                {inputCell('subsoilPOSFill')}
              </tr>
              {/* Attenuation Tanks — user input */}
              <tr>
                <td style={tdLabelStyle}>Attenuation Tanks</td>
                {inputCell('subsoilAttenuationTanksCut')}
                {inputCell('subsoilAttenuationTanksFill')}
              </tr>
              {/* Culverts — user input */}
              <tr>
                <td style={tdLabelStyle}>Culverts</td>
                {inputCell('subsoilCulvertsCut')}
                {inputCell('subsoilCulvertsFill')}
              </tr>
              {/* Mains Services — user input */}
              <tr>
                <td style={tdLabelStyle}>Mains Services</td>
                {inputCell('subsoilMainsServicesCut')}
                {inputCell('subsoilMainsServicesFill')}
              </tr>
            </tbody>
            <tfoot>
              <tr style={totalRowStyle}>
                <td style={{ ...tdLabelStyle, ...totalRowStyle }}>
                  Total
                  <InfoIcon title="SUM of every row above: Road, Paths, Foul Sewers, Storm Sewers, Ponds, POS, Attenuation Tanks, Culverts, Mains Services." />
                </td>
                <td style={{ ...tdCalcStyle, ...totalRowStyle }}>{totalSubsoilCut.toFixed(3)}</td>
                <td style={{ ...tdCalcStyle, ...totalRowStyle }}>{totalSubsoilFill.toFixed(3)}</td>
              </tr>
              <tr>
                <td style={{ ...tdLabelStyle, fontWeight: 700, borderBottom: 'none' }}>
                  Surplus / Deficit
                  <InfoIcon title="Total Cut − Total Fill. Positive = Surplus (disposal needed); Negative = Deficit (import needed)." />
                </td>
                <td colSpan={2} style={{ ...tdCalcStyle, fontWeight: 700, borderBottom: 'none', borderRight: 'none', backgroundColor: '#fff' }}>
                  <span style={{ marginRight: '10px' }}>{subsoilSurplusDeficit.toFixed(3)}</span>
                  {renderBalanceBadge(subsoilSurplusDeficit)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Footer */}
        <div style={{ borderTop: '1px solid #e5e7eb' }}>
          <div className="px-6 py-4 flex justify-end gap-3">
            <Button
              onClick={handleSaveAndExit}
              style={{ borderColor: COLORS.primary, color: COLORS.primary }}
            >
              Save and Exit
            </Button>
            <Button
              type="primary"
              onClick={handleSaveAndContinue}
              style={{ backgroundColor: COLORS.primary, borderColor: COLORS.primary }}
            >
              Save &amp; Continue <ArrowRightOutlined />
            </Button>
          </div>
        </div>
      </Card>

      <ExcelImportModal
        onImport={handleImport}
        onDownloadTemplate={() => {/* template not needed */}}
        importLoading={importLoading}
        importModalVisible={importModalVisible}
        setImportModalVisible={setImportModalVisible}
      />

      <Modal
        title="Details"
        open={!!activeInfo}
        onCancel={() => setActiveInfo(null)}
        footer={[<Button key="close" onClick={() => setActiveInfo(null)}>Close</Button>]}
        width={480}
      >
        {activeInfo && (
          <div style={{ fontSize: 13, color: '#374151' }}>{activeInfo.title}</div>
        )}
      </Modal>

      <style>{`
        .spoil-card { overflow: visible !important; }
        .spoil-card .ant-card-body { overflow: visible !important; }
        .spoil-sticky-header { box-shadow: none; will-change: transform; transform: translateZ(0); z-index: 98 !important; }
      `}</style>
    </MainLayout>
  );
};

export default SpoilReconciliation;
